import type {CSSProperties} from 'vue'
type ColorValue = string | null

/**
 * Styler class to build styles based on Tailwind CSS conventions.
 *
 * It allows for dynamic styling based on existing classes and provides methods to set text color, background color, and sizing.
 *
 * Usage:
 * ```javascript
 * import { useStyles } from '@/composables/useStyles'
 * const { build } = useStyles()
 * const styles = build()
 *   .textColor('blue')
 *   .backgroundColor(['gray', 200])
 *   .sizing(5)
 *   .build()
 *
 *   // This will generate a style object that can be applied to a component.
 *   console.log(styles)
 *   * // Output: { color: var(--color-blue), backgroundColor: var(--color-gray-200), width: calc(var(--spacing) * 5), height: calc(var(--spacing) * 5) }
 * ```
 *
 * The builder can also watch for existing classes to avoid overriding them. Finally, the builder will only add styles if the necessary css variables are
 * predefined. To ensure this, define theme colors statically in the tailwind css file.
 */

function isCssNumericValue(value: string): boolean {
    return /^[+|-]?\d+(\.\d+)?$/.test(value)
}

function parseColorValue(color: string | [string, number]): {color: string | null, value: number | null} {
    let value: number | null = null

    // allow array input like ['blue', 500]
    if (!!color && Array.isArray(color)) {
        value = color[1]
        color = color[0]
    }

    // allow color string with value as gray-500
    if(!!color && color.includes('-')) {
        const parts = color.split('-')
        if(parts.length > 0 && parts[0]) color = parts[0]
        value = parts.length > 1 ? parseInt(parts[1] ?? '') : null
    }

    return {color: color ?? null, value}
}

class Styler {
    color: string | null = null
    background: string | null = null
    width: string | null = null
    height: string | null = null
    preExistingClass: string | null = null

    get(): CSSProperties {
        const classes = this.preExistingClass?.split(' ') ?? []
        const textColorClassExists = classes.some(c => c.startsWith('text-'))
        const backgroundColorClassExists = classes.some(c => c.startsWith('bg-'))
        const sizingClassExists = classes.some(c => c.startsWith('size-') || c.startsWith('w-') || c.startsWith('h-'))

        const style: CSSProperties = {}

        if (this.color && !textColorClassExists) {
            style.color = this.color
        }
        if (this.background && !backgroundColorClassExists) {
            style.backgroundColor = this.background
        }
        if (this.width && !sizingClassExists) {
            style.width = this.width
        }
        if (this.height && !sizingClassExists) {
            style.height = this.height
        }

        return style
    }

    watchFor(classes: string) {
        this.preExistingClass = classes
        return this
    }

    textColor(color?: string | [string, number]) {
        this.color = this.colorize(color)
        return this
    }

    backgroundColor(color?: string | [string, number]) {
        this.background = this.colorize(color)
        return this
    }

    colorize(color: null | string | [string, number] = null): ColorValue {
        if(!color) return null
        const {color: colorStr, value} = parseColorValue(color)
        const moduleColor = $getModuleColor(colorStr)
        if (moduleColor) {
            return useCssVar(`--color-${moduleColor}` + (value ? `-${value}` : '')).value ? `var(--color-${moduleColor}` + (value ? `-${value}` : '') + `)` : null
        }

        return null
    }

    sizing(size: string | null = null) {
        // check if size is an integer or float and not a css value (xxxrem, xxxpx, etc)
        if (size && isCssNumericValue(size)) {
            this.width = `calc(var(--spacing) * ${size})`
            this.height = `calc(var(--spacing) * ${size})`
        }
        return this
    }

    widen(size: string | null = null) {
        if (size && isCssNumericValue(size)) {
            this.width = `calc(var(--spacing) * ${size})`
        }
        return this
    }

    heighten(size: string | null = null) {
        if (size && isCssNumericValue(size)) {
            this.height = `calc(var(--spacing) * ${size})`
        }
        return this
    }

    definedColor(color?: null | string | [string, number]) {
        if(!color) return null
        const {color: colorStr, value} = parseColorValue(color)
        const moduleColor = $getModuleColor(colorStr)
        if (moduleColor) return !!useCssVar(`--color-${moduleColor}` + (value ? `-${value}` : '')).value
        return false
    }

    definedSize(size?: string | null) {
        if (!size) return false
        return !!useCssVar(`--spacing`).value && isCssNumericValue(size)
    }
}

export const useStyles = () => {
    return {
        build: () => new Styler(),
        definedColor: new Styler().definedColor,
        definedSize: new Styler().definedSize,
    }
}
